import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useSupabase } from '../supabaseAuth';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [showMfaInput, setShowMfaInput] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaFactorId, setMfaFactorId] = useState('');
  const [savedEmail, setSavedEmail] = useState('');
  const [savedPassword, setSavedPassword] = useState('');
  const { login, loginWithSocial, loginWithMFA, loading, error, isAuthenticated, authProcessing } = useSupabase();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';
  const [message, setMessage] = useState(location.state?.message || '');

  // Check for MFA requirement from sessionStorage on mount
  useEffect(() => {
    const mfaRequired = sessionStorage.getItem('mfa_required');
    if (mfaRequired) {
      try {
        const mfaData = JSON.parse(mfaRequired);
        setSavedEmail(mfaData.email);
        setSavedPassword(mfaData.password);
        setMfaFactorId(mfaData.factorId);
        setShowMfaInput(true);
        setFormError('Please enter your two-factor authentication code');
        sessionStorage.removeItem('mfa_required');
      } catch (error) {
        console.error('Error parsing MFA data from sessionStorage:', error);
        sessionStorage.removeItem('mfa_required');
      }
    }
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !showMfaInput && !authProcessing) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate, showMfaInput, authProcessing]);

  // Reset MFA input when navigating away from login
  useEffect(() => {
    return () => {
      if (showMfaInput) {
        setShowMfaInput(false);
        setMfaCode('');
        setSavedEmail('');
        setSavedPassword('');
        setMfaFactorId('');
      }
    };
  }, [showMfaInput]);

  // Clear message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!email || !password) {
      setFormError('Please enter both email and password');
      return;
    }

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Login error:', error);

      if (error.requiresMFA) {
        setSavedEmail(error.email || email);
        setSavedPassword(error.password || password);
        setMfaFactorId(error.factorId);
        setShowMfaInput(true);
        setFormError('Please enter your two-factor authentication code');
      } else {
        setFormError(error.message || 'Failed to login. Please check your credentials.');
      }
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!mfaCode || mfaCode.length !== 6) {
      setFormError('Please enter a valid 6-digit code');
      return;
    }

    try {
      await loginWithMFA(savedEmail, savedPassword, mfaFactorId, mfaCode);
      navigate(from, { replace: true });
    } catch (error) {
      console.error('MFA verification error:', error);
      setFormError(error.message || 'Invalid verification code. Please try again.');
    }
  };

  const handleSocialLogin = async (provider) => {
    try {
      await loginWithSocial(provider);
    } catch (error) {
      console.error(`${provider} login error:`, error);
      setFormError(error.message || `Failed to login with ${provider}`);
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 pt-20 md:pt-24">
      <main className="w-full flex flex-col gap-2.5 items-center p-5 md:p-12 md:pt-0">
        {/* Page Header */}
        <h1 className="text-3xl font-bold text-black mb-4">Log In</h1>

        {/* Login Container */}
      <div className="bg-white rounded-2xl shadow-md w-full max-w-2xl flex flex-col items-center gap-3 p-8 md:p-10">
        <div className="flex flex-col w-full max-w-md gap-4 flex-1">

          {/* Welcome Section */}
          <div className="flex flex-col items-center text-center w-full mb-2">
            <h2 className="text-[26px] font-semibold text-black mb-1">Welcome Back!</h2>
            <p className="text-gray-600 text-base leading-relaxed">
              Sign in to access your precious metals portfolio
            </p>
          </div>

          {/* Messages */}
          {message && (
            <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg">
              {message}
            </div>
          )}

          {(error || formError) && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {formError || error}
            </div>
          )}

          {/* Form Section */}
          {!showMfaInput ? (
            <form onSubmit={handleSubmit} className="flex flex-col justify-between flex-1">
              <div className="flex flex-col gap-2.5">
                {/* Email Field */}
                <div className="flex flex-col">
                  <label className="font-medium mb-1">Email Address</label>
                  <input
                    type="email"
                    className="border border-gray-300 rounded-lg p-3 text-[0.95rem] transition-all focus:outline-none focus:border-black focus:ring-2 focus:ring-gray-100"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    required
                    autoFocus
                  />
                </div>

                {/* Password Field */}
                <div className="flex flex-col">
                  <label className="font-medium mb-1">Password</label>
                  <div className="relative flex items-center">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="w-full border border-gray-300 rounded-lg p-3 text-[0.95rem] transition-all focus:outline-none focus:border-black focus:ring-2 focus:ring-gray-100"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 bg-transparent border-none text-gray-500 cursor-pointer"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i className={`bi bi-eye${showPassword ? '-slash' : ''}`}></i>
                    </button>
                  </div>
                </div>

                {/* Forgot Password */}
                <div className="flex justify-end">
                  <Link
                    to="/forgot-password"
                    className="text-black no-underline font-bold hover:underline"
                  >
                    Forgot Password?
                  </Link>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex flex-col items-center w-full gap-3 mt-4">
                <button
                  type="submit"
                  className="w-full h-12 rounded-full bg-black text-white font-bold text-base border-none hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  {loading ? 'Logging in...' : 'Log in'}
                </button>

                {/* Divider */}
                <div className="flex items-center text-center w-full my-1">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <span className="px-4 text-gray-700 text-base">Or</span>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>

                {/* Google Sign In */}
                <button
                  type="button"
                  onClick={() => handleSocialLogin('google')}
                  className="flex items-center justify-center bg-white border-2 border-black rounded-full h-12 w-full text-black font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  <svg className="w-[18px] h-[18px] mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue With Google
                </button>

                {/* Sign Up Link */}
                <div className="text-center text-gray-700">
                  Don't have an account?{' '}
                  <Link
                    to="/signup"
                    className="text-black no-underline font-bold hover:underline"
                  >
                    Sign Up
                  </Link>
                </div>
              </div>
            </form>
          ) : (
            // MFA Form
            <form onSubmit={handleMfaSubmit} className="flex flex-col justify-between flex-1">
              <div className="flex flex-col gap-4">
                <div className="text-center mb-4">
                  <i className="bi bi-shield-lock text-5xl text-black mb-3"></i>
                  <h3 className="text-xl font-semibold">Two-Factor Authentication</h3>
                  <p className="text-gray-600">Enter the 6-digit code from your authenticator app</p>
                </div>

                <div className="flex flex-col">
                  <label className="font-medium mb-1">Verification Code</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg p-3 text-2xl text-center tracking-[0.5em] transition-all focus:outline-none focus:border-black focus:ring-2 focus:ring-gray-100"
                    placeholder="000000"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    disabled={loading}
                    required
                    autoFocus
                    maxLength={6}
                    autoComplete="one-time-code"
                    inputMode="numeric"
                  />
                </div>
              </div>

              <div className="flex flex-col items-center w-full gap-3 mt-4">
                <button
                  type="submit"
                  className="w-full h-12 rounded-full bg-black text-white font-bold text-base border-none hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || mfaCode.length !== 6}
                >
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowMfaInput(false);
                    setMfaCode('');
                    setFormError('');
                    setSavedEmail('');
                    setSavedPassword('');
                    setMfaFactorId('');
                  }}
                  className="w-full h-12 rounded-full bg-white text-gray-700 font-bold text-base border-2 border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  Back to Login
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      </main>
    </div>
  );
};

export default Login;
